import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDocs, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getProjectPath } from '../utils/collectionPaths';
import { getGlobalSettings } from '../services/apiService';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

const TaskContext = createContext();

const timestampToMillis = (value) => {
  if (!value) return Date.now();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  if (typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1000000);
  }
  return Date.now();
};

export const useTaskContext = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTaskContext must be used within a TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState({});
  const [projectId, setProjectId] = useState(null);
  const [refreshCallback, setRefreshCallback] = useState(null);
  const listenersRef = useRef(new Map()); // Store Firestore listeners
  // Refs (not state) — registering a callback should NOT trigger a re-render.
  // Using state caused the entire useCallback chain (setupTaskListener → setupNewTasksListener → restoreRunningTasks)
  // to re-create on every register/unregister, which cascaded into ProjectViewPage's effects and produced
  // "Maximum update depth exceeded" warnings on the Actors tab.
  const immediateRefreshCallbacksRef = useRef(new Map());
  const taskCompletionCallbacksRef = useRef(new Map());

  // Get current user from AuthContext
  const { currentUser } = useAuth();

  // Get notification function if available
  const { showNotification } = useNotification();

  // Track which tasks have already had mail created to prevent duplicates
  const mailCreatedRef = useRef(new Set());

  // Function to create mail document when task is completed
  const createTaskCompletedMail = useCallback(async (taskData, projectId, userEmail) => {
    const taskId = taskData.id;
    // Check if mail has already been created for this task
    if (mailCreatedRef.current.has(taskId)) {
      return;
    }
    
    try {
      // Check global settings for email notifications
      let _shouldSendEmail = true;
      try {
        const globalSettings = await getGlobalSettings();
        
        // Determine automation type based on task action and description
        let automationType = null;
        const action = taskData.action || '';
        const description = taskData.description || '';
        const combinedText = `${action} ${description}`.toLowerCase();
        if (combinedText.includes('description')) {
          automationType = 'descriptions';
        } else if (combinedText.includes('audience')) {
          automationType = 'audiences';
        } else if (combinedText.includes('actor')) {
          automationType = 'actors';
        } else if (combinedText.includes('question')) {
          automationType = 'questions';
        } else if (combinedText.includes('interview')) {
          automationType = 'interviews';
        } else if (combinedText.includes('ai actor') || combinedText.includes('ai_actor')) {
          automationType = 'aiActor';
        } else if (combinedText.includes('strategy block')) {
          automationType = 'strategyBlocks';
        } else if (combinedText.includes('ai strategy') || combinedText.includes('ai_strategy')) {
          automationType = 'aiStrategy';
        } else if (combinedText.includes('structured output') || combinedText.includes('screen')) {
          automationType = 'structuredOutput';
        } else if (combinedText.includes('image')) {
          automationType = 'imageGeneration';
        }
        // Check if the specific automation type is enabled
        if (automationType && !globalSettings.emailNotifications?.automations?.[automationType]) {
          return;
        }
      } catch (error) {
        // Default to true if we can't get settings
        _shouldSendEmail = true;
      }
      
      const mailData = {
        template: {
          name: "task_completed",
          data: {
            taskDescription: taskData.description || taskData.action || "Task completed",
            taskLink: `https://app.segmently.ai/${projectId}`
          }
        },
        to: [userEmail]
      };
      await addDoc(collection(db, 'mail'), mailData);
      
      // Mark this task as having mail created
      mailCreatedRef.current.add(taskId);
    } catch (error) {
      // Silent fail for email notifications
    }
  }, [currentUser]);

  // Clean up Firestore listeners
  const cleanupListeners = useCallback(() => {
    listenersRef.current.forEach((unsubscribe) => {
      unsubscribe();
    });
    listenersRef.current.clear();
  }, []);

  // Enhanced function to trigger immediate refresh for all registered callbacks
  const triggerImmediateRefresh = useCallback((taskId, taskData) => {
    // Trigger the main refresh callback
    if (refreshCallback) {
      setTimeout(() => {
        refreshCallback();
      }, 100); // Small delay to ensure UI updates first
    }

    // Trigger all registered immediate refresh callbacks (read from ref — see comment on the ref decl)
    immediateRefreshCallbacksRef.current.forEach((callback) => {
      try {
        callback(taskId, taskData);
      } catch (error) {
        // Silent fail for callback errors
      }
    });
  }, [refreshCallback]);

  // Function to trigger task completion callbacks
  const triggerTaskCompletionCallbacks = useCallback((taskId, taskData) => {
    taskCompletionCallbacksRef.current.forEach((callback) => {
      try {
        callback(taskId, taskData);
      } catch (error) {
        // Silent fail for callback errors
      }
    });
  }, []);

  // Set up Firestore listener for a specific task
  const setupTaskListener = useCallback((taskId, projectId, userId) => {
    if (!projectId || !userId) return;
    
    // Clean up existing listener if any
    if (listenersRef.current.has(taskId)) {
      listenersRef.current.get(taskId)();
      listenersRef.current.delete(taskId);
    }

    // Set up new listener
    const taskDocRef = doc(db, `projects/${projectId}/backend_tasks/${taskId}`);
    const unsubscribe = onSnapshot(taskDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const taskData = docSnapshot.data();
        
        setTasks(prev => {
          const updated = { ...prev };
          if (updated[taskId]) {
            const previousStatus = updated[taskId].status;
            
            // Only update status, result, and error from Firestore
            const updatedTask = {
              ...updated[taskId],
              status: taskData.status,
              result: taskData.result || null,
              error: taskData.error || null
            };
            
            updated[taskId] = updatedTask;
            
            // Show notification when task status changes to final state
            if (previousStatus !== taskData.status && ['completed', 'error', 'cancelled'].includes(taskData.status)) {
              const taskName = updated[taskId].description || updated[taskId].action || 'Task';
              if (taskData.status === 'completed') {
                // Show notification (NotificationContext will check global settings)
                if (showNotification) {
                  showNotification(`${taskName} completed successfully!`, 'success');
                }
                
                // Create mail document for completed task
                if (currentUser && currentUser.email) {
                  createTaskCompletedMail(updated[taskId], projectId, currentUser.email);
                }
                
                // Trigger immediate refresh for all automation tasks
                const taskText = `${updated[taskId].action || ''} ${updated[taskId].description || ''}`.toLowerCase();
                if (taskText.includes('ai strategy') || 
                    taskText.includes('ai strategy block') ||
                    taskText.includes('creating') ||
                    taskText.includes('generating') ||
                    taskText.includes('conducting') ||
                    taskText.includes('interview')
                ) {
                  triggerImmediateRefresh(taskId, updated[taskId]);
                }
                
                // Always trigger task completion callbacks for any completed task
                triggerTaskCompletionCallbacks(taskId, updated[taskId]);
              } else if (taskData.status === 'error') {
                if (showNotification) {
                  showNotification(`${taskName} failed: ${taskData.error || 'Unknown error'}`, 'error');
                }
                // Trigger completion callbacks for error tasks
                triggerTaskCompletionCallbacks(taskId, updated[taskId]);
              } else if (taskData.status === 'cancelled') {
                if (showNotification) {
                  showNotification(`${taskName} was cancelled.`, 'warning');
                }
                // Trigger completion callbacks for cancelled tasks
                triggerTaskCompletionCallbacks(taskId, updated[taskId]);
              }
            }
            
            // If task is completed, error, or cancelled, clean up listener
            if (['completed', 'error', 'cancelled'].includes(taskData.status)) {
              // Clean up listener
              if (listenersRef.current.has(taskId)) {
                listenersRef.current.get(taskId)();
                listenersRef.current.delete(taskId);
              }
            }
          }
          return updated;
        });
      } else {
        // Document doesn't exist yet, but don't remove the task immediately
        // The task might still be in the process of being created by the backend

        // Don't remove the task from local state - let it stay visible in the sidebar
        // The listener will be cleaned up when the task reaches a final status
      }
    }, (error) => {
      // Clean up listener on error
      if (listenersRef.current.has(taskId)) {
        listenersRef.current.get(taskId)();
        listenersRef.current.delete(taskId);
      }
    });

    // Store the unsubscribe function
    listenersRef.current.set(taskId, unsubscribe);
  }, [refreshCallback, createTaskCompletedMail, triggerImmediateRefresh, showNotification, currentUser]);

  // Set up listener for new tasks in backend_tasks collection
  const setupNewTasksListener = useCallback((projectId, userId) => {
    if (!projectId || !userId) return;
    
    // Clean up existing new tasks listener if any
    if (listenersRef.current.has('new_tasks_listener')) {
      listenersRef.current.get('new_tasks_listener')();
      listenersRef.current.delete('new_tasks_listener');
    }

    // Set up listener for new tasks
    const backendTasksRef = collection(db, `projects/${projectId}/backend_tasks`);
    const q = query(backendTasksRef, where('userId', '==', userId));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const taskId = change.doc.id;
          const taskData = change.doc.data();
          
          // Only add tasks that are running (newly created)
          if (taskData.status === 'running') {
            setTasks(prev => {
              // Check if task already exists in local state
              if (prev[taskId]) {
                return prev;
              }
                            
              const newTask = {
                id: taskId,
                status: taskData.status,
                action: taskData.action || taskData.description || taskData.action_description || 'Task',
                startTime: timestampToMillis(taskData.createdAt),
                result: taskData.result || null,
                error: taskData.error || null,
                projectId: projectId,
                userId: userId,
              };
              
              return {
                ...prev,
                [taskId]: newTask
              };
            });
            
            // Set up individual task listener for this new task
            // Only set up listener if one doesn't already exist
            if (!listenersRef.current.has(taskId)) {
              setupTaskListener(taskId, projectId, userId);
            }
          }
        } else if (change.type === 'modified') {
          const taskId = change.doc.id;
          const taskData = change.doc.data();

          // Update the task in local state
          setTasks(prev => {
            if (prev[taskId]) {
              const previousStatus = prev[taskId].status;
              const updated = { ...prev };
              updated[taskId] = {
                ...updated[taskId],
                status: taskData.status,
                result: taskData.result || null,
                error: taskData.error || null
              };
              
              // Check if task just completed and trigger refresh
              if (previousStatus !== 'completed' && taskData.status === 'completed') {
                // Show notification when task status changes to final state
                const taskName = updated[taskId].description || updated[taskId].action || 'Task';
                if (taskData.status === 'completed') {
                  // Show notification (NotificationContext will check global settings)
                  if (showNotification) {
                    showNotification(`${taskName} completed successfully!`, 'success');
                  }
                  
                  // Create mail document for completed task
                  if (currentUser && currentUser.email) {
                    createTaskCompletedMail(updated[taskId], projectId, currentUser.email);
                  }
                  
                  // Trigger immediate refresh for all automation tasks
                  const taskText = `${updated[taskId].action || ''} ${updated[taskId].description || ''}`.toLowerCase();
                  if (taskText.includes('ai strategy') || 
                      taskText.includes('ai strategy block') ||
                      taskText.includes('creating') ||
                      taskText.includes('generating') ||
                      taskText.includes('conducting') ||
                      taskText.includes('interview')
                  ) {
                    triggerImmediateRefresh(taskId, updated[taskId]);
                  }
                  
                  // Always trigger task completion callbacks for any completed task
                  triggerTaskCompletionCallbacks(taskId, updated[taskId]);
                } else if (taskData.status === 'error') {
                  if (showNotification) {
                    showNotification(`${taskName} failed: ${taskData.error || 'Unknown error'}`, 'error');
                  }
                  // Trigger completion callbacks for error tasks
                  triggerTaskCompletionCallbacks(taskId, updated[taskId]);
                } else if (taskData.status === 'cancelled') {
                  if (showNotification) {
                    showNotification(`${taskName} was cancelled.`, 'warning');
                  }
                  // Trigger completion callbacks for cancelled tasks
                  triggerTaskCompletionCallbacks(taskId, updated[taskId]);
                }
              }
              
              return updated;
            }
            return prev;
          });
        }
      });
    }, (error) => {
      // Clean up listener on error
      if (listenersRef.current.has('new_tasks_listener')) {
        listenersRef.current.get('new_tasks_listener')();
        listenersRef.current.delete('new_tasks_listener');
      }
    });

    // Store the unsubscribe function
    listenersRef.current.set('new_tasks_listener', unsubscribe);
  }, [setupTaskListener, currentUser, showNotification, createTaskCompletedMail, triggerImmediateRefresh, triggerTaskCompletionCallbacks, projectId]);

  // Restore running tasks from Firestore
  const restoreRunningTasks = useCallback(async (currentProjectId, currentUserId) => {
    if (!currentProjectId || !currentUserId) return;
    
    try {
      // Query backend_tasks collection for running tasks for this user and project
      const backendTasksRef = collection(db, `projects/${currentProjectId}/backend_tasks`);
      const q = query(backendTasksRef, where('userId', '==', currentUserId));
      const querySnapshot = await getDocs(q);
      
      const restoredTasks = {};
      
      querySnapshot.forEach((docSnapshot) => {
        const taskData = docSnapshot.data();
        const taskId = docSnapshot.id; // The document ID is the task ID
        
        // Only restore tasks that are still running (not completed, error, or cancelled)
        if (taskData.status === 'running' || taskData.status === 'cancelling') {
          restoredTasks[taskId] = {
            id: taskId,
            status: taskData.status,
            action: taskData.action || taskData.description || taskData.action_description || 'Task',
            startTime: timestampToMillis(taskData.createdAt),
            result: taskData.result || null,
            error: taskData.error || null,
            projectId: currentProjectId,
            userId: currentUserId,
          };
          
          // Set up Firestore listener for all running tasks
          // Only set up listener if one doesn't already exist
          if (!listenersRef.current.has(taskId)) {
            setupTaskListener(taskId, currentProjectId, currentUserId);
          }
        }
      });
      
      if (Object.keys(restoredTasks).length > 0) {
        setTasks(restoredTasks);
        setProjectId(currentProjectId);
      }
    } catch (error) {
      // Silent fail for task restoration
    }
  }, [setupTaskListener, currentUser]);

  // Restore running tasks when component mounts or projectId changes
  useEffect(() => {
    if (projectId && currentUser?.uid) {
      restoreRunningTasks(projectId, currentUser.uid);
      
      // Also set up listener for new tasks
      setupNewTasksListener(projectId, currentUser.uid);
    }
  }, [projectId, currentUser, restoreRunningTasks, setupNewTasksListener]);

  // Restore running tasks for all projects (for dashboard)
  const restoreAllRunningTasks = useCallback(async (currentUserId) => {
    if (!currentUserId) return;
    
    try {
      // Get all projects the user is a member of
      const projectsRef = collection(db, 'projects');
      const projectsSnapshot = await getDocs(projectsRef);
      
      const restoredTasks = {};
      
      // For each project, check for running tasks
      for (const projectDoc of projectsSnapshot.docs) {
        const projectId = projectDoc.id;
        
        // Check if user is a member of this project
        const memberRef = doc(db, `projects/${projectId}/members/${currentUserId}`);
        const memberSnap = await getDocs(memberRef);
        
        if (memberSnap.exists()) {
          // User is a member, check for running tasks
          const backendTasksRef = collection(db, `projects/${projectId}/backend_tasks`);
          const q = query(backendTasksRef, where('userId', '==', currentUserId));
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((docSnapshot) => {
            const taskData = docSnapshot.data();
            const taskId = docSnapshot.id; // The document ID is the task ID
            
            // Only restore tasks that are still running
            if (taskData.status === 'running' || taskData.status === 'cancelling') {
              restoredTasks[taskId] = {
                id: taskId,
                status: taskData.status,
                action: `${taskData.description || 'Task'}`,
                startTime: timestampToMillis(taskData.createdAt),
                result: taskData.result || null,
                error: taskData.error || null,
                projectId: projectId,
                userId: currentUserId,
              };
              
              // Set up Firestore listener for all running tasks
              // Only set up listener if one doesn't already exist
              if (!listenersRef.current.has(taskId)) {
                setupTaskListener(taskId, projectId, currentUserId);
              }
            }
          });
        }
      }
      
      if (Object.keys(restoredTasks).length > 0) {
        setTasks(restoredTasks);
      }
      
    } catch (error) {
      // Silent fail for task restoration
    }
  }, [setupTaskListener]);

  // Cancel a running task
  const cancelTask = useCallback(async (taskId) => {
    const task = tasks[taskId];
    if (!task) {
      return;
    }

    if (!['running', 'cancelling'].includes(task.status)) {
      return;
    }

    try {
      // Update task status to cancelling in local state immediately
      setTasks(prev => {
        const updated = { ...prev };
        if (updated[taskId]) {
          updated[taskId] = {
            ...updated[taskId],
            status: 'cancelling'
          };
        }
        return updated;
      });

      // Update the task document directly using taskId as document ID
      if (task.projectId) {
        const taskDocRef = doc(db, `projects/${task.projectId}/backend_tasks/${taskId}`);
        await updateDoc(taskDocRef, {
          status: 'cancelled',
          cancelReason: 'User cancelled',
          cancelledAt: serverTimestamp()
        });
      } else {
        throw new Error(`Task has no projectId`);
      }
    } catch (error) {
      // Revert status if Firestore update fails
      setTasks(prev => {
        const updated = { ...prev };
        if (updated[taskId]) {
          updated[taskId] = {
            ...updated[taskId],
            status: 'running' // Revert to running status
          };
        }
        return updated;
      });
    }
  }, [tasks]);

  // Start a new task and add it to the context (for backward compatibility)
  const startTask = useCallback((taskId, action) => {
    if (!taskId) {
      return;
    }
    // Add the task to the context immediately (external webhook will create the actual document)
    setTasks(prev => {
      const updated = { ...prev };
      updated[taskId] = {
        id: taskId,
        status: 'running',
        action: action || 'Processing task...',
        startTime: Date.now(),
        result: null,
        error: null,
        projectId: projectId,
        userId: currentUser?.uid,
        isAITask: true
      };
      return updated;
    });

    // Set up Firestore listener for the new task with a delay to allow external webhook to create the document
    if (projectId && currentUser?.uid) {
      setTimeout(() => {
        // Only set up listener if one doesn't already exist
        if (!listenersRef.current.has(taskId)) {
          setupTaskListener(taskId, projectId, currentUser.uid);
        }
      }, 2000); // Wait 2 seconds for external webhook to create the task document
    }
  }, [projectId, currentUser, setupTaskListener]);

  // Remove a completed/cancelled/error task
  const removeTask = useCallback((taskId) => {
    // Clean up Firestore listener if it exists
    if (listenersRef.current.has(taskId)) {
      listenersRef.current.get(taskId)();
      listenersRef.current.delete(taskId);
    }
    
    // Clean up mail tracking
    mailCreatedRef.current.delete(taskId);
    
    setTasks(prev => {
      const newTasks = { ...prev };
      delete newTasks[taskId];
      return newTasks;
    });
  }, [tasks]);

  // Clear project task status when all tasks are done
  const clearProjectTaskStatus = useCallback(async () => {
    if (projectId) {
      try {
        const projectRef = doc(db, getProjectPath(projectId));
        await updateDoc(projectRef, { 
          taskStatus: null,
          taskType: null
        });
      } catch (firestoreError) {
        // Silent fail for project status clearing
      }
    }
  }, [projectId]);

  // Clear project task status when all tasks are completed
  useEffect(() => {
    const runningTasks = Object.values(tasks).filter(task => 
      ['running', 'cancelling'].includes(task.status)
    );
    
    if (runningTasks.length === 0 && Object.keys(tasks).length > 0) {
      // All tasks are done, clear project task status after a short delay
      const timer = setTimeout(() => {
        clearProjectTaskStatus();
      }, 2000); // Wait 2 seconds to ensure UI has time to update
      
      return () => clearTimeout(timer);
    }
  }, [tasks, clearProjectTaskStatus]);

  // Auto-remove completed tasks after a delay
  useEffect(() => {
    const completedTasks = Object.values(tasks).filter(task => 
      ['completed', 'error'].includes(task.status)
    );
    
    const cancelledTasks = Object.values(tasks).filter(task => 
      task.status === 'cancelled'
    );

    // Remove cancelled tasks immediately (no delay)
    if (cancelledTasks.length > 0) {
      cancelledTasks.forEach(task => {
        removeTask(task.id);
      });
    }
    
    // Remove completed tasks after a longer delay (5 seconds)
    if (completedTasks.length > 0) {
      const completedTimer = setTimeout(() => {
        completedTasks.forEach(task => {
          removeTask(task.id);
        });
      }, 5000); // Wait 5 seconds to show completion status
      
      return () => {
        clearTimeout(completedTimer);
      };
    }
  }, [tasks, removeTask]);

  // Clean up listeners on unmount
  useEffect(() => {
    return () => {
      cleanupListeners();
    };
  }, [cleanupListeners]);

  // Stable selectors. Hooks at top level (not inline in `value` literal) so they keep their
  // identity across renders.
  const getRunningTasks = useCallback(() => {
    return Object.values(tasks).filter(task =>
      ['running', 'cancelling'].includes(task.status)
    );
  }, [tasks]);

  const getCompletedTasks = useCallback(() => {
    return Object.values(tasks).filter(task =>
      ['completed', 'cancelled', 'error'].includes(task.status)
    );
  }, [tasks]);

  const registerImmediateRefreshCallback = useCallback((key, callback) => {
    immediateRefreshCallbacksRef.current.set(key, callback);
  }, []);

  const unregisterImmediateRefreshCallback = useCallback((key) => {
    immediateRefreshCallbacksRef.current.delete(key);
  }, []);

  const registerTaskCompletionCallback = useCallback((key, callback) => {
    taskCompletionCallbacksRef.current.set(key, callback);
  }, []);

  const unregisterTaskCompletionCallback = useCallback((key) => {
    taskCompletionCallbacksRef.current.delete(key);
  }, []);

  // Memoize so that consumers using `useTaskContext()` don't re-render on every render of the provider.
  const value = useMemo(() => ({
    tasks,
    startTask,
    cancelTask,
    removeTask,
    setCurrentProjectId: setProjectId,
    clearProjectTaskStatus,
    restoreRunningTasks,
    restoreAllRunningTasks,
    setRefreshCallback,
    getRunningTasks,
    getCompletedTasks,
    registerImmediateRefreshCallback,
    unregisterImmediateRefreshCallback,
    registerTaskCompletionCallback,
    unregisterTaskCompletionCallback,
  }), [
    tasks,
    startTask,
    cancelTask,
    removeTask,
    clearProjectTaskStatus,
    restoreRunningTasks,
    restoreAllRunningTasks,
    getRunningTasks,
    getCompletedTasks,
    registerImmediateRefreshCallback,
    unregisterImmediateRefreshCallback,
    registerTaskCompletionCallback,
    unregisterTaskCompletionCallback,
  ]);

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
}; 
